import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import dayjs from "dayjs";
import { moodColor, moodLevel } from "@/lib/config";
import { api } from "@/lib/api";
import { fmtMins, recoveryStats, themeGrid } from "@/lib/triggerInsights";
import { ThemeInsight } from "@/components/ThemeInsight";

const inkOn = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return ((n >> 16) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000 > 120 ? "#1B2A3B" : "#ffffff";
};

const Card = ({ title, sub, children, testId }) => (
  <div className="tf-card p-5" data-testid={testId}>
    <p className="font-bold text-t-on">{title}</p>
    {sub ? <p className="text-sm text-t-muted mt-0.5 mb-3">{sub}</p> : <div className="mb-3" />}
    {children}
  </div>
);

function useThemeSorting(sessions) {
  const qc = useQueryClient();
  const busy = useRef(false);
  const pending = sessions.filter((s) => s.triggerNote && !s.triggerTheme).length;
  useEffect(() => {
    if (!pending || busy.current) return;
    busy.current = true;
    api.post("/ai/trigger-themes").then(({ data }) => data.updated && qc.invalidateQueries({ queryKey: ["sessions"] })).catch(() => {}).finally(() => { busy.current = false; });
  }, [pending, qc]);
  return pending;
}

export function TriggerMoodTrends({ sessions }) {
  const pending = useThemeSorting(sessions);
  const g = useMemo(() => themeGrid(sessions), [sessions]);
  return (
    <Card title="How triggers affect your mood" sub="Your triggers, sorted into themes by AI · average mood each month as a result of the trigger" testId="insight-trigger-trends">
      {g.rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-1" data-testid="trigger-trends-grid">
            <thead><tr><th className="text-left font-semibold text-t-muted pr-2">Theme</th>{g.cols.map((m) => <th key={m} className="font-semibold text-t-muted text-xs w-12">{dayjs(m + "-01").format("MMM")}</th>)}</tr></thead>
            <tbody>
              {g.rows.map((r, i) => (
                <tr key={r.theme} data-testid={`trigger-theme-row-${i}`}>
                  <td className="pr-2 text-t-on2 whitespace-nowrap">{r.theme} <span className="text-t-muted">· {r.n}</span></td>
                  {r.cells.map((c, j) => (
                    <td key={j} title={c ? `${moodLevel(Math.round(c.v))?.label} (${c.v.toFixed(1)}) · ${c.n} time${c.n === 1 ? "" : "s"}` : "No triggers"}
                      className="h-9 rounded-lg text-center text-xs font-bold transition-transform hover:scale-105"
                      style={c ? { background: moodColor(Math.round(c.v)), color: inkOn(moodColor(Math.round(c.v))) } : { background: "var(--surface3)" }}>{c ? Math.round(c.v) : ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {g.hardest ? <p className="mt-3 text-sm text-t-on2" data-testid="trigger-trends-hardest"><b>{g.hardest.theme}</b> tends to hit hardest — average mood {g.hardest.avg.toFixed(1)}. Naming it is the first step to meeting the need underneath.</p> : null}
          {g.hardest ? <ThemeInsight theme={g.hardest.theme} count={g.hardest.n} /> : null}
        </div>
      ) : <p className="text-sm text-t-muted">{pending ? "Sorting your triggers into themes…" : "Note what triggered you during a check-in to see which themes affect your mood most."}</p>}
    </Card>
  );
}

export function RecoveryTime({ sessions }) {
  const r = useMemo(() => recoveryStats(sessions), [sessions]);
  return (
    <Card title="Time to get back to calm" sub="From when you reached out until distress was calm (0-3) or your mood was neutral or better" testId="insight-recovery">
      {r.count ? (
        <>
          <p className="text-t-on" data-testid="recovery-average">Average <b>{fmtMins(r.avg)}</b> across {r.count} check-in{r.count === 1 ? "" : "s"}</p>
          <div className="h-44 mt-3">
            <ResponsiveContainer>
              <BarChart data={r.trend} margin={{ left: -24, right: 8, top: 8 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <Tooltip formatter={(v) => [fmtMins(v), "Avg time to calm"]} contentStyle={{ borderRadius: 12 }} />
                <Bar dataKey="mins" fill="var(--success)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {r.message ? <p className="mt-2 text-sm text-t-on2" data-testid="recovery-message">{r.message}</p> : null}
        </>
      ) : <p className="text-sm text-t-muted">Finish a check-in after being triggered to start tracking how quickly you settle.</p>}
    </Card>
  );
}
